import { Color } from '@persona-id/design-tokens';
import { sum } from 'lodash';
import styled from 'styled-components';

import { NAVBAR_EXPANDED_WIDTH } from 'dashboard/components/Navbar';
import { FunnelNode } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTree';
import { MIN_CONNECTOR_HEIGHT } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/StepFlow';
import { BASE } from 'design-system/variables/spacing';

export const StepFlowContainer = styled('div')`
  min-width: calc(100vw - ${NAVBAR_EXPANDED_WIDTH}px);
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transform-origin: 0 0;
  padding: 64px;
`;

export const StepFlowStepContainer = styled('div')`
  display: flex;
  align-items: top;
  flex-direction: row;
  min-height: auto;
`;

export const CONNECTOR_BORDER_WIDTH = 4;
export const HORIZONTAL_LINE_TOP_MARGIN = 20;

const lineHeight = (height: number, strokeRatio: number) => {
  const attemptedLineHeight = height * strokeRatio;
  if (isNaN(attemptedLineHeight)) {
    return MIN_CONNECTOR_HEIGHT;
  }
  return Math.max(MIN_CONNECTOR_HEIGHT, attemptedLineHeight);
};

export const StepFlowHorizontalLine = styled('div')<{
  width?: number;
  height: number;
  color?: string;
  borderColor: string;
  strokeRatio: number;
}>`
  ${({ color, height, strokeRatio, borderColor }) => {
    const effectiveHeight = lineHeight(height, strokeRatio);
    const lineColor = color ?? Color.CorePurple200;
    return `
      width: 100%;
      height: ${effectiveHeight}px;
      margin-top: ${HORIZONTAL_LINE_TOP_MARGIN / 2}px;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0px,
        ${lineColor} 0px,
        ${lineColor} ${effectiveHeight}px,
        rgba(0, 0, 0, 0) ${1}px
      );
      border-right: ${CONNECTOR_BORDER_WIDTH}px solid ${borderColor};
      border-left: ${CONNECTOR_BORDER_WIDTH}px solid ${borderColor};
    `;
  }}
`;

export const tokenFromStepAndBranch = (
  parentStep: { token: string; branches?: Pick<FunnelNode, 'pathRatio'>[][] },
  index: number,
) => {
  return `${parentStep.token}-branch-${index}`;
};

export const StepFlowBranchContainer = styled('div')<{ blur: boolean }>`
  opacity: ${({ blur }) => (blur ? 0.25 : 1)};
  transition: opacity 0.5s;
  display: flex;
  align-items: center;
`;

export const StepFlowBranchStepsContainer = styled('div')`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

export const StepFlowBranchSubTree = styled('div')<{ height?: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${({ height }) => height}px;
`;

export const StepFlowBranchEmptySpacer = styled('div')<{ height: number }>`
  height: ${({ height }) => height}px;
`;

export type StepPosition = {
  height: number;
};

export const buildStepHeights = (
  steps: FunnelNode[],
  stepContentHeight: number,
  stepTokenToPositionMap: Map<string, StepPosition>,
): number => {
  if (steps.length === 0) {
    return stepContentHeight + BASE * 2;
  }

  const [currentStep, ...restSteps] = steps;

  if (Array.isArray(currentStep.branches)) {
    const branchHeights = currentStep.branches.map((branch, index) => {
      const branchHeight = buildStepHeights(branch, stepContentHeight, stepTokenToPositionMap);
      stepTokenToPositionMap.set(tokenFromStepAndBranch(currentStep, index), {
        height: branchHeight,
      });
      return branchHeight;
    });

    const childrenHeights = sum(branchHeights);

    const nextHeight = buildStepHeights(restSteps, stepContentHeight, stepTokenToPositionMap);
    const nodesHeight = Math.max(childrenHeights, nextHeight);
    stepTokenToPositionMap.set(currentStep.token, { height: nodesHeight });
    return nodesHeight;
  } else {
    const nodesHeight = buildStepHeights(restSteps, stepContentHeight, stepTokenToPositionMap);
    stepTokenToPositionMap.set(currentStep.token, { height: nodesHeight });
    return nodesHeight;
  }
};
